/**
 * Lift items: whether a car raises an item, who acts, the band, and the
 * alerts that are its evidence (twin slice 1, plan W5 step 8; contract
 * note §4).
 *
 * W6 OWNS THE READING. `liftReading()` and `liftSiteRow()`
 * (`src/screens/Devices/lift-reading.ts`, W6) decide what a car is doing
 * and write its Guests, Robots and who-acts lines and its "since".
 * `LiftReadingInput` is a STRUCTURAL SUBSET of W6's `LiftReading` -- same
 * kind names, same field names -- plus the facts the reading does not
 * carry, so the hook passes `{ ...liftReading(...), robotUse, level, fault,
 * session, waitingRobots, carLevel }` with no translation. W5 writes NO
 * lift text of its own: an item's lines, who-acts line and "since" are
 * W6's, verbatim.
 *
 * ONE OWNER FOR THE ATTENTION COLUMN. The contract note (§4) gives W5 who
 * acts, the band and whether the attention list raises an item. W5 decides
 * them FROM W6's proposal (`whoActs`, `raisesItem`), so the drawer and the
 * card cannot disagree about who acts:
 *   - `reading` kinds raise exactly when W6's `raisesItem` says, with W6's
 *     who acts (`lift_company` is a call the person on site makes, so it
 *     is "you");
 *   - the two timed kinds are W5's own (Decision 8, ideation §3b): a car
 *     held for a robot is carded at 5 min seen, as Info ("Nobody yet"); a
 *     hall wait shows at 3 min, and at 10 min asks for a call;
 *   - every other kind never raises a card of its own -- healthy,
 *     passive, carrying a robot, or said once by the site row -- whatever
 *     the reading proposes.
 * `LIFT_KIND_POLICY` is exhaustive over W6's kinds, so a new kind does not
 * compile until it has a policy.
 *
 * DURATIONS are counted from when THIS PAGE first saw the condition, on
 * the device clock; no adapter stamp is ever compared with `Date.now()`.
 * That is why the ideation's "or once past its own deadline" is NOT
 * implemented: `wait.deadline_epoch_ts` is the adapter's wall clock, and
 * comparing it with the device clock is the cross-clock count risk 14
 * forbids. It needs a remaining-seconds figure on the adapter's own
 * monotonic clock; until then the 10 min rule alone asks for the call.
 *
 * EVIDENCE KEYS are what the server WOULD raise for this reading, read
 * from the same facts its rules read (`liftAlertKeys`): a promise that
 * "Nexsoss is told automatically" is made only for a rule that will fire.
 */
import type { LiftSessionView } from '../../api/v1';
import type { AlertKey } from './evidence';
import { LANES_CLOSED_NOT_ALERTED, LIFT_REFUSAL_NOT_ALERTED } from './lift-codes';
import type { Band, WhoActs } from './robot-copy';

/**
 * W6's `LiftReadingKind` (`lift-reading.ts` at W6 b7c58ff), verbatim and
 * in W6's order. At the M9 rebase onto W6 this list is checked against
 * W6's own union (`attention-lift-items.test.ts`, "W6's vocabulary").
 */
export const LIFT_READING_KINDS = [
  'no_reading',
  'checking',
  'fire_service',
  'cant_check',
  'control_off',
  'emergency',
  'lift_offline',
  'kept_out',
  'not_taking_robots',
  'refusing',
  'robot_held',
  'robot_in_use',
  'robot_waiting',
  'operator_held',
  'working',
] as const;
export type LiftReadingKind = (typeof LIFT_READING_KINDS)[number];

/** W6's `LiftWhoActs`: who should act, as far as the reading alone says. */
export const LIFT_WHO_ACTS = ['nobody', 'you', 'nexsoss', 'lift_company'] as const;
export type LiftWhoActs = (typeof LIFT_WHO_ACTS)[number];

export type RobotUse = 'between_floors' | 'same_floor' | 'none';

export interface LiftRobotRef {
  fleet: string;
  name: string;
}

/** One car, as the attention list reads it. */
export interface LiftReadingInput {
  // -- W6's `LiftReading`: the fields read here, by W6's names ------------
  readonly name: string;
  readonly kind: LiftReadingKind;
  readonly guests: string;
  readonly robots: string;
  readonly whoActs: LiftWhoActs;
  readonly whoActsLine: string;
  /** W6's proposal: the reading alone warrants an item. */
  readonly raisesItem: boolean;
  /** W6's `LiftLostReason`, for `no_reading`; null otherwise. */
  readonly lostBecause: string | null;
  /** The start W6's lines cite, from a field with its own stamp. */
  readonly sinceEpochMs: number | null;
  // -- facts the reading does not carry ------------------------------------
  /** The asset's `robot.use` (W1 schema 2.0). */
  readonly robotUse: RobotUse;
  /** The floor a landing for this car lands on (W6 `liftLandingLevel`). */
  readonly level: string | null;
  /**
   * The floor the CAR reports standing at (`current_level`), for a robot
   * inside it: a robot's own pose names the floor it boarded on, so its
   * card takes the car's floor instead (`select.ts::robotFloor`). Null or
   * absent when that is not established -- the car moving, its reading
   * not fresh -- and then the card names no floor. Never `level` above,
   * which may be a waiting robot's floor. Optional: until the hook passes
   * it, a robot in a car is on no named floor.
   */
  readonly carLevel?: string | null;
  /** `LiftState.fault`: what the server's `lift_fault` reads. Null when
   *  the car has no row. */
  readonly fault: boolean | null;
  /** The adapter session, read here only for what the server's lift rules
   *  read: the refusal's standing, and the closure. */
  readonly session: Pick<
    LiftSessionView,
    'lanes_closed' | 'lanes_closed_code' | 'commandable' | 'refusal'
  > | null;
  /** Robots that own or wait on the car's session. */
  readonly waitingRobots: readonly LiftRobotRef[];
}

/** W6's `LiftSiteRowKind`. */
export type LiftSiteRowKind =
  | 'fire'
  | 'lift_control_off'
  | 'cant_check'
  | 'cant_change_floors'
  | 'cant_tell';

/** W6's single building-wide lift row (`liftSiteRow()`), the fields read
 *  here. */
export interface LiftSiteRowInput {
  readonly kind: LiftSiteRowKind;
  /** The row, for every audience. */
  readonly text: string;
  /** Ops only (Details): what the FOH row leaves out. */
  readonly opsNote: string | null;
  /** The cars the row is about. W6 names none for the two gate rows,
   *  which are about every car; see `siteRowCars`. */
  readonly lifts: readonly string[];
}

/** The per-car reading each gate row speaks for, every car of that kind. */
const SITE_ROW_READING: Partial<Record<LiftSiteRowKind, LiftReadingKind>> = {
  lift_control_off: 'control_off',
  cant_check: 'cant_check',
};

/** The cars a site row speaks for: the ones it names, and for a gate row
 *  every car whose reading is that gate's. */
export function siteRowCars(
  row: LiftSiteRowInput,
  readings: readonly LiftReadingInput[],
): LiftReadingInput[] {
  const kind = SITE_ROW_READING[row.kind];
  return readings.filter((r) => row.lifts.includes(r.name) || (kind !== undefined && r.kind === kind));
}

export const HELD_CARD_AFTER_MS = 5 * 60_000;
export const WAIT_SHOWN_AFTER_MS = 3 * 60_000;
export const WAIT_CALL_AFTER_MS = 10 * 60_000;

/** How W5 decides a kind (see the module comment). */
export type LiftKindPolicy = 'reading' | 'held' | 'waiting' | 'never';

export const LIFT_KIND_POLICY: Readonly<Record<LiftReadingKind, LiftKindPolicy>> = {
  // W6 raises it for a robot car, and not for this screen's own feed clamp.
  no_reading: 'reading',
  checking: 'never',
  // Passive: the fire panel has it.
  fire_service: 'never',
  // The site row says these once, never a card per car.
  cant_check: 'never',
  control_off: 'never',
  emergency: 'reading',
  lift_offline: 'reading',
  kept_out: 'reading',
  not_taking_robots: 'reading',
  // Only once escalated or permanent (W6's `raisesItem`).
  refusing: 'reading',
  robot_held: 'held',
  // Carrying a robot. Decision 8 sets no threshold for a long ride; one is
  // not established, so it raises nothing here.
  robot_in_use: 'never',
  robot_waiting: 'waiting',
  // An operator's hold ends by itself (`auto_close_epoch_ts`).
  operator_held: 'never',
  working: 'never',
};

/** W6's who acts, as the attention list groups it. */
export const WHO_FROM_READING: Readonly<Record<LiftWhoActs, WhoActs>> = {
  nobody: 'none',
  you: 'you',
  nexsoss: 'nexsoss',
  // The person on site makes that call.
  lift_company: 'you',
};

export interface LiftAttention {
  whoActs: WhoActs;
  band: Band;
  /** W6's who-acts line, verbatim; null once W5's own timing has moved
   *  who acts past what the line says. */
  whoActsLine: string | null;
  /** Steps for the person on site. W6's line carries the instruction;
   *  the only step W5 adds is the card's "nothing to press" for an item
   *  Nexsoss acts on (ideation §3b). */
  steps: string[];
  /** W6's start stamp (epoch ms), or null. */
  sinceMs: number | null;
  /** The alerts that mean the server has raised this. */
  alertKeys: AlertKey[];
}

/**
 * The debounce key for a car's condition, or null when this reading cannot
 * raise anything. A guest car NEVER raises (Decision 3): it has no robot
 * meaning, and a card about it would ask FOH to manage the building's
 * lifts. A timed kind is keyed from first sight, so that "seen for" can
 * reach its threshold.
 */
export function liftConditionKey(reading: LiftReadingInput): string | null {
  if (reading.robotUse === 'none') return null;
  const policy = LIFT_KIND_POLICY[reading.kind];
  if (policy === 'never') return null;
  if (policy === 'reading' && !reading.raisesItem) return null;
  return `lift:${reading.name}:${reading.kind}`;
}

function key(rule: string, lift: string): AlertKey {
  return { rule, sourceKind: 'lift', sourceId: lift };
}

/**
 * The lift alerts the server raises for THIS reading, from the facts its
 * rules read -- not from the kind, and not "every lift rule":
 *
 *   lift_fault          `fault` is true (`alerts_engine._on_lift_state`),
 *                       except on a car W6 reads as `control_off`. The
 *                       worker counts MODE_OFFLINE as a fault and every car
 *                       reads it while the gate is disarmed or unvalidated
 *                       (the connector folds the gate into `offline`), so
 *                       the server reads the site's gate and raises nothing
 *                       for an `off` car behind a shut one. W6's
 *                       `control_off` is that reading. And for a car robots
 *                       don't use, only its own emergency mode: the server
 *                       judges an `off` reading for robot lifts alone
 *                       (`_lift_fault_judged`). Its 15 s grace for `off` is
 *                       `SERVER_RULE_DWELLS.lift_fault`. Left over: a car
 *                       whose own reading is lost or checking behind a shut
 *                       gate still lists it -- the reading cannot say which
 *                       it is -- and an emergency mode behind an
 *                       unvalidated gate, which W6 reads as control off and
 *                       the server still raises (it counts among "other
 *                       open alerts").
 *   lift_refusal        a refusal that is escalated or permanent, with a
 *                       code that is neither the write gate nor an operator
 *                       verb (`_evaluate_lift_refusal`).
 *   lift_lanes_closed   W7's rule: lanes closed while the car says it can
 *                       take robot calls (`commandable === true`), no
 *                       fault, and a closure code it does not leave to
 *                       another alert. Off by default; `evidenceFor` makes
 *                       no promise for it unless it is switched on.
 *   lift_no_reading     W7's rule: the car's reading is lost at its source
 *                       or has stopped arriving -- not this screen's own
 *                       feed clamp. Off by default, likewise.
 */
export function liftAlertKeys(reading: LiftReadingInput): AlertKey[] {
  const lift = reading.name;
  const s = reading.session;
  const keys: AlertKey[] = [];
  if (
    reading.fault === true &&
    reading.kind !== 'control_off' &&
    (reading.robotUse !== 'none' || reading.kind === 'emergency')
  ) {
    keys.push(key('lift_fault', lift));
  }
  const refusal = s?.refusal;
  if (
    refusal &&
    refusal.code &&
    (refusal.permanent || refusal.escalated === true) &&
    !LIFT_REFUSAL_NOT_ALERTED.has(refusal.code)
  ) {
    keys.push(key('lift_refusal', lift));
  }
  if (
    s?.lanes_closed === true &&
    s.commandable === true &&
    reading.fault !== true &&
    typeof s.lanes_closed_code === 'string' &&
    !LANES_CLOSED_NOT_ALERTED.has(s.lanes_closed_code)
  ) {
    keys.push(key('lift_lanes_closed', lift));
  }
  if (reading.kind === 'no_reading' && reading.lostBecause !== 'feed_clamped') {
    keys.push(key('lift_no_reading', lift));
  }
  return keys;
}

/** The server's lift-control rule (amendment A14) and the source it
 *  stamps: the site's lift write gate, by the site's id. */
export const LIFT_GATE_ALERT_RULE = 'lift_gate_disarmed';
export const LIFT_GATE_SOURCE_KIND = 'lift_write_gate';

/**
 * The alerts the server raises for W6's site row ITSELF, beside its cars'
 * (`liftAlertKeys`): for "Lift control off", `lift_gate_disarmed` --
 * the site's gate read shut for 120 s outside a planned maintenance
 * window, off by default (`SERVER_RULE_DWELLS`). With it open the row
 * says Nexsoss has been alerted; without it, a disarm's cars raise
 * nothing (the server reads the gate and pages no car), so the row asked
 * for a call. No other row is about the gate. Empty with no site id: the
 * alert is keyed on it, and a key that names no site matches nothing.
 */
export function siteRowAlertKeys(row: LiftSiteRowInput, siteId: string | null | undefined): AlertKey[] {
  if (row.kind !== 'lift_control_off' || !siteId) return [];
  return [{ rule: LIFT_GATE_ALERT_RULE, sourceKind: LIFT_GATE_SOURCE_KIND, sourceId: siteId }];
}

const NOTHING_TO_PRESS = 'Nothing for you to press.';

const finiteOrNull = (ms: number | null): number | null =>
  typeof ms === 'number' && Number.isFinite(ms) ? ms : null;

/**
 * How long a car's condition must have been seen before it has a card:
 * 0 for a `reading` kind (the debounce's raise is the card); 5 min for a
 * car held for a robot ("Nobody yet. Carded at 5 min."); 3 min for a hall
 * wait; null for a kind that never has one. The "Looks fixed" and
 * "Cleared at" cards read it too: a car whose condition ended before its
 * card was ever up has nothing to look fixed.
 */
export function liftCardAfterMs(kind: LiftReadingKind): number | null {
  switch (LIFT_KIND_POLICY[kind]) {
    case 'reading':
      return 0;
    case 'held':
      return HELD_CARD_AFTER_MS;
    case 'waiting':
      return WAIT_SHOWN_AFTER_MS;
    case 'never':
      return null;
  }
}

/**
 * Who acts on a car of this kind once its condition has been seen for
 * `seenForMs`: W6's who acts, except that a hall wait asks for a call at
 * 10 min.
 */
export function liftWhoActs(kind: LiftReadingKind, fromW6: LiftWhoActs, seenForMs: number): WhoActs {
  if (LIFT_KIND_POLICY[kind] === 'waiting' && seenForMs >= WAIT_CALL_AFTER_MS) return 'nexsoss';
  return WHO_FROM_READING[fromW6];
}

/**
 * Whether this car raises an item NOW, and how.
 *
 * `seenSinceMs` is when this page first saw the condition (device clock);
 * null if it has not seen it long enough to say. Returns null when the car
 * raises nothing.
 */
export function liftAttention(
  reading: LiftReadingInput,
  seenSinceMs: number | null,
  nowMs: number,
): LiftAttention | null {
  if (liftConditionKey(reading) === null) return null;
  const seenFor = seenSinceMs === null ? 0 : nowMs - seenSinceMs;
  const after = liftCardAfterMs(reading.kind);
  // A `reading` kind is carded by the raise alone, whatever the clocks say.
  if (after === null || (after > 0 && seenFor < after)) return null;
  const whoActs = liftWhoActs(reading.kind, reading.whoActs, seenFor);
  return {
    whoActs,
    band: 'blocked',
    // W6's line says who acts as the reading alone sees it. Once W5's own
    // timing has moved who acts past it (a wait at 10 min asks for a
    // call, where W6 says "Nobody yet"), it is no longer what the card
    // means, so it is not shown.
    whoActsLine: whoActs === WHO_FROM_READING[reading.whoActs] ? reading.whoActsLine : null,
    steps: whoActs === 'nexsoss' ? [NOTHING_TO_PRESS] : [],
    sinceMs: finiteOrNull(reading.sinceEpochMs),
    alertKeys: liftAlertKeys(reading),
  };
}

/**
 * The car can't be read now (amendment A7): no reading (lost at its
 * source, receipts stopped, or this screen's own feed clamped), seen only
 * once (`checking`: a REST seed or a replay is not yet a reading), or the
 * write gate it is read through can't be read (`cant_check`) or is off
 * (`control_off`). W6 reads a car's robot conditions only behind an armed
 * gate, and while the gate is off the connector publishes mode off for
 * every car whether its link is up or down: a disarm masks "Kept out", it
 * does not fix it. A condition such a car no longer shows has not gone:
 * nothing says so. Every other kind is a reading.
 */
export const LIFT_UNREAD_KINDS: ReadonlySet<LiftReadingKind> = new Set<LiftReadingKind>([
  'no_reading',
  'checking',
  'cant_check',
  'control_off',
]);

/**
 * Whether W6's site row NOW (`now`) leaves an earlier row of kind `was`
 * unread (A7): the row now says what decides `was` can't be read.
 *
 *   cant_check        the write gate can't be read, so neither can any
 *                     row the gate decides (lift control off) or that
 *                     needs the gate read first (W6 reads the cars only
 *                     once it is armed).
 *   lift_control_off  the gate was read, and it is off, so W6 no longer
 *                     reads the cars: a row about what they say ("robots
 *                     can't change floors", "can't tell") is masked, not
 *                     fixed. "Can't check" is not unread: the gate it
 *                     could not read now reads.
 *   cant_tell         a between-floors car has no trustworthy reading and
 *                     none reads usable, so "robots can't change floors"
 *                     can be neither confirmed nor ruled out. Lift control
 *                     off is not unread: the gate it is about was read,
 *                     and is armed.
 *
 * Any other row now -- none, or one that reads -- can say: no row means a
 * between-floors car reads usable (or the site has none), and that IS the
 * fix.
 */
export function siteRowUnread(was: LiftSiteRowKind, now: LiftSiteRowKind | null): boolean {
  if (now === 'cant_check') return was !== 'cant_check';
  if (now === 'lift_control_off') return was === 'cant_change_floors' || was === 'cant_tell';
  if (now === 'cant_tell') return was === 'cant_change_floors';
  return false;
}

/** Who acts on W6's site row when none of its cars says otherwise. */
export const SITE_ROW_WHO_ACTS: Readonly<Record<LiftSiteRowKind, WhoActs>> = {
  fire: 'none',
  lift_control_off: 'nexsoss',
  cant_check: 'none',
  cant_change_floors: 'nexsoss',
  cant_tell: 'nexsoss',
};
